import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { TraceabilityService } from '../services/traceability.service';
import { TraceabilityResponse, TraceabilityDeepResponse, TraceabilityRun } from '../services/traceability.interfaces';

@Component({
  selector: 'lib-trace',
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    ToggleSwitchModule,
    PaginatorModule,
  ],
  templateUrl: './trace.component.html',
  standalone: true,
})
export class TraceComponent implements OnInit {
  private traceabilityService = inject(TraceabilityService);

  identifier = signal<string>('');
  selectedField = signal<string>('any');
  result = signal<TraceabilityResponse | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);
  expandedRuns = signal<Set<number | string>>(new Set());

  showFilters = signal(false);
  selectedStatuses = signal<string[]>([]);
  dateFrom = signal<string>('');
  dateTo = signal<string>('');
  deepMode = signal(false);
  deepResult = signal<TraceabilityDeepResponse | null>(null);
  currentPage = signal(1);
  readonly pageSize = 20;

  readonly statusOptions = [
    { label: 'Exitoso', value: 'success' },
    { label: 'Error', value: 'error' },
    { label: 'Omitido', value: 'omitted' },
    { label: 'Procesando', value: 'processing' },
  ];

  fieldOptions = signal<string[]>([]);

  readonly fieldSelectOptions = computed(() => [
    { label: 'Cualquier campo', value: 'any' },
    ...this.fieldOptions().map((key) => ({ label: key, value: key })),
  ]);

  readonly breadcrumbs: MenuItem[] = [
    { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
    { label: 'Historial de registro' },
  ];

  ngOnInit() {
    this.traceabilityService.getSearchOptions().subscribe({
      next: (options) => {
        this.fieldOptions.set(options.origin_keys);
      },
      error: (err) => {
        console.error('Error al cargar opciones de búsqueda:', err);
      },
    });
  }

  search() {
    const term = this.identifier().trim();
    if (!term) return;

    if (this.deepMode()) {
      this.searchDeep(term);
      return;
    }

    this.currentPage.set(1);
    this.runSearch();
  }

  private runSearch() {
    const term = this.identifier().trim();
    if (!term) return;

    this.loading.set(true);
    this.error.set(null);
    this.deepResult.set(null);

    const field = this.selectedField();
    const statuses = this.selectedStatuses();
    const searchParams = {
      identifier: term,
      ...(field !== 'any' && { field }),
      ...(statuses.length > 0 && { status: statuses }),
      ...(this.dateFrom() && { dateFrom: this.dateFrom() }),
      ...(this.dateTo() && { dateTo: this.dateTo() }),
      page: this.currentPage(),
      pageSize: this.pageSize,
    };

    this.traceabilityService.search(searchParams).subscribe({
      next: (response) => {
        this.result.set(response);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Error al buscar el registro');
        this.loading.set(false);
        console.error(err);
      },
    });
  }

  goToPage(page: number) {
    const total = this.result()?.executions_count ?? 0;
    const totalPages = Math.ceil(total / this.pageSize);
    if (page < 1 || page > totalPages) return;
    this.currentPage.set(page);
    this.runSearch();
  }

  onPageChange(event: PaginatorState) {
    const first = event.first ?? 0;
    const page = Math.floor(first / this.pageSize) + 1;
    this.goToPage(page);
  }

  get totalPages(): number {
    const total = this.result()?.executions_count ?? 0;
    return Math.ceil(total / this.pageSize) || 1;
  }

  searchDeep(term: string) {
    if (!this.dateFrom() || !this.dateTo()) {
      this.error.set('La búsqueda profunda requiere un rango de fechas (Desde y Hasta).');
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.result.set(null);

    this.traceabilityService
      .searchDeep({
        identifier: term,
        dateFrom: this.dateFrom(),
        dateTo: this.dateTo(),
      })
      .subscribe({
        next: (response) => {
          this.deepResult.set(response);
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set('Error al buscar el registro en modo profundo');
          this.loading.set(false);
          console.error(err);
        },
      });
  }

  toggleRun(runId: number | string | null) {
    if (runId === null) return;
    const current = new Set(this.expandedRuns());
    if (current.has(runId)) {
      current.delete(runId);
    } else {
      current.add(runId);
    }
    this.expandedRuns.set(current);
  }

  isExpanded(runId: number | string | null): boolean {
    if (runId === null) return false;
    return this.expandedRuns().has(runId);
  }

  getRunKey(run: TraceabilityRun): number | string {
    return run.execution_list_id ?? `orphan-${run.steps[0]?.execution_id}`;
  }

  toggleFilters() {
    this.showFilters.set(!this.showFilters());
  }

  toggleStatus(value: string) {
    const current = this.selectedStatuses();
    if (current.includes(value)) {
      this.selectedStatuses.set(current.filter((s) => s !== value));
    } else {
      this.selectedStatuses.set([...current, value]);
    }
  }

  isStatusSelected(value: string): boolean {
    return this.selectedStatuses().includes(value);
  }

  removeStatusChip(value: string) {
    this.toggleStatus(value);
    this.search();
  }

  clearDateFrom() {
    this.dateFrom.set('');
    this.search();
  }

  clearDateTo() {
    this.dateTo.set('');
    this.search();
  }

  activeFiltersCount(): number {
    return (
      this.selectedStatuses().length +
      (this.dateFrom() ? 1 : 0) +
      (this.dateTo() ? 1 : 0) +
      (this.selectedField() !== 'any' ? 1 : 0) +
      (this.deepMode() ? 1 : 0)
    );
  }

  clearFilters() {
    this.selectedField.set('any');
    this.selectedStatuses.set([]);
    this.dateFrom.set('');
    this.dateTo.set('');
    this.deepMode.set(false);
    this.search();
  }

  hasActiveFilters(): boolean {
    return (
      this.selectedStatuses().length > 0 ||
      !!this.dateFrom() ||
      !!this.dateTo()
    );
  }

  retryingRuns = signal<Set<number>>(new Set());
  retrySuccessMessage = signal<string | null>(null);

  retryRun(workflowId: number | null, executionListId: number | null) {
    if (workflowId === null || executionListId === null) return;

    const current = new Set(this.retryingRuns());
    current.add(executionListId);
    this.retryingRuns.set(current);
    this.retrySuccessMessage.set(null);

    this.traceabilityService.retryWorkflow(workflowId, executionListId).subscribe({
      next: () => {
        const updated = new Set(this.retryingRuns());
        updated.delete(executionListId);
        this.retryingRuns.set(updated);
        this.retrySuccessMessage.set('Reintento enviado exitosamente');
        setTimeout(() => this.retrySuccessMessage.set(null), 4000);
      },
      error: () => {
        const updated = new Set(this.retryingRuns());
        updated.delete(executionListId);
        this.retryingRuns.set(updated);
        this.error.set('No se pudo enviar el reintento');
      },
    });
  }

  isRetrying(executionListId: number | null): boolean {
    if (executionListId === null) return false;
    return this.retryingRuns().has(executionListId);
  }

  getStatusBadgeClass(status: string): string {
    const value = (status || '').toLowerCase();
    if (value === 'success') {
      return 'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    }
    if (value === 'error') {
      return 'border-red-200 bg-red-100 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300';
    }
    if (value === 'processing') {
      return 'border-blue-200 bg-blue-100 text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    }
    if (value === 'omitted' || value === 'skipped') {
      return 'border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
    return 'border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }

  getStatusDotClass(status: string): string {
    const value = (status || '').toLowerCase();
    if (value === 'success') return 'bg-emerald-500';
    if (value === 'error') return 'bg-red-500';
    if (value === 'processing') return 'bg-blue-500';
    if (value === 'omitted' || value === 'skipped') return 'bg-gray-400';
    return 'bg-gray-400';
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      success: 'Exitoso',
      error: 'Error',
      omitted: 'Omitido',
      skipped: 'Omitido',
      processing: 'Procesando',
    };
    return labels[status] || status;
  }

  openHistoryPage(workflowId: number | null, executionListId: number | null, executionId?: number) {
    if (workflowId === null || executionListId === null) return;
    let url = `/monitoreo/workflow/${workflowId}/execution/${executionListId}/history`;
    if (executionId) {
      url += `?openId=${executionId}`;
    }
    window.open(url, '_blank');
  }
}